/**
 * 評価画面のふりかえり（#14）: 採点項目を課題文のタスクごとにまとめる。
 * 患者面接タブと報告タブで同じ形を使い、面接中の課題文インジケーターと同じ規則でタスクへ振り分ける。
 * 聞き方・提案・報告の例は課題文の正解例（item_examples）から出し、評価のたびには作らない。
 */
import type {
  DentistReportItemScore,
  DentistReportScoringResult,
  InstructionItemExamples,
  ItemScore,
  ScoringResult,
  TermFinding,
} from "../types";
import { selectTaskItems, type ProgressSourceTask } from "./interview-task-progress";

export type ReviewItemStatus =
  /** 患者面接: 聞けた / 報告: 自分から報告できた */
  | "done"
  /** 報告: 聞き返されてから報告した */
  | "assisted"
  /** 患者面接: まだ聞けていない / 報告: 聞けたのに報告していない */
  | "missing"
  /** 報告: 患者面接で聞けていないので報告しようがない */
  | "not-heard";

export interface ReviewItem {
  itemKey: string;
  label: string;
  level: string;
  status: ReviewItemStatus;
  /** 聞けた・報告できた内容 */
  evidence?: string;
  /** 患者の設定（聞けていれば分かったこと） */
  expectedText?: string;
  /** 聞き方・提案・報告の例（anyOf は①②③の数だけ） */
  examples?: string[];
}

export interface ReviewTask {
  /** 課題文のタスク番号。コミュニケーションは 0、タスクに結べない項目の束は -1 */
  no: number;
  title: string;
  items: ReviewItem[];
  got: number;
  total: number;
  done: boolean;
  /** 報告タブで、どの項目も患者面接で聞けていない */
  notHeard: boolean;
}

export interface ReviewSide {
  tasks: ReviewTask[];
  termFindings: TermFinding[];
}

interface ScoredItem {
  itemKey: string;
  categoryId: string;
  level: string;
  columnKey?: string;
  item: ReviewItem;
}

const HIGHLIGHT_MARKER = /\[\[\/?HL\]\]/g;

/** 患者面接のコミュニケーション（C0）は講座によらないので、課題文ではなくここに例を持つ。 */
const COMMUNICATION_EXAMPLES: Record<string, string> = {
  Lv1: "こんにちは。今日はよろしくお願いいたします。",
  Lv2: "本日担当いたします、歯科衛生士学生の〇〇です。",
  Lv3: "お名前をフルネームで教えていただけますか？",
  Lv4: "生年月日を教えていただけますか？",
  Lv5: "これからお口のことについて、いくつか伺ってもよろしいですか？",
  Lv6: "それはご心配でしたね。",
};

function stripMarkers(text: string | undefined): string | undefined {
  const cleaned = text?.replace(HIGHLIGHT_MARKER, "").trim();
  return cleaned || undefined;
}

function labelOf(score: ItemScore | DentistReportItemScore): string {
  return score.v31?.groupLabel || score.v31?.label || score.itemName;
}

/** 課題文の正解例を引く key。anyOf は採点と同じく groupKey でまとまっている。 */
function exampleKeyOf(score: ItemScore | DentistReportItemScore): string | undefined {
  const v31 = score.v31;
  if (!v31) return undefined;
  if (v31.scoringPolicy === "anyOf" && v31.groupKey) return v31.groupKey;
  return v31.itemKey;
}

function toTask(no: number, title: string, items: ReviewItem[]): ReviewTask {
  const got = items.filter((item) => item.status === "done" || item.status === "assisted").length;
  return {
    no,
    title,
    items,
    got,
    total: items.length,
    done: items.length > 0 && got >= items.length,
    notHeard: items.length > 0 && items.every((item) => item.status === "not-heard"),
  };
}

function groupByTask(scored: ScoredItem[], tasks: ProgressSourceTask[] | null | undefined): ReviewTask[] {
  const communication = scored.filter((entry) => entry.categoryId === "C0");
  const topics = scored.filter((entry) => entry.categoryId !== "C0");
  const assigned = new Set<string>();
  const result: ReviewTask[] = [];
  (tasks ?? []).forEach((task, index) => {
    const items = selectTaskItems(task, topics, (entry) => entry);
    items.forEach((entry) => assigned.add(entry.itemKey));
    if (items.length > 0) result.push(toTask(task.no ?? index + 1, task.title, items.map((entry) => entry.item)));
  });
  const rest = topics.filter((entry) => !assigned.has(entry.itemKey));
  if (rest.length > 0) {
    result.push(toTask(-1, result.length > 0 ? "課題文のタスク以外の項目" : "聞き取り", rest.map((entry) => entry.item)));
  }
  if (communication.length > 0) {
    result.push(toTask(0, "コミュニケーション", communication.map((entry) => entry.item)));
  }
  return result;
}

function scoredItem(score: ItemScore | DentistReportItemScore, item: ReviewItem): ScoredItem | null {
  const v31 = score.v31;
  if (!v31?.itemKey || !v31.categoryId) return null;
  return {
    itemKey: v31.itemKey,
    categoryId: v31.categoryId,
    level: v31.levelKey,
    columnKey: v31.columnKey,
    item,
  };
}

/** 患者面接タブ: 項目ごとに聞けたか、聞けていなければ聞き方・提案の例と患者の設定。 */
export function buildPatientReview(
  result: ScoringResult | null | undefined,
  tasks: ProgressSourceTask[] | null | undefined,
  itemExamples?: InstructionItemExamples | null,
): ReviewSide | null {
  if (!result?.itemScores?.length) return null;
  const scored = result.itemScores
    .map((score) => {
      const captured = score.score > 0;
      const key = exampleKeyOf(score);
      const communication = score.v31?.categoryId === "C0" ? COMMUNICATION_EXAMPLES[score.v31.levelKey] : undefined;
      const examples = captured ? undefined : (key ? itemExamples?.[key]?.ask : undefined) ?? (communication ? [communication] : undefined);
      return scoredItem(score, {
        itemKey: score.v31?.itemKey ?? score.itemId,
        label: labelOf(score),
        level: score.v31?.levelKey ?? "",
        status: captured ? "done" : "missing",
        evidence: captured ? score.evidence || undefined : undefined,
        expectedText: captured ? undefined : stripMarkers(score.v31?.expectedText),
        examples,
      });
    })
    .filter((entry): entry is ScoredItem => entry !== null);
  if (scored.length === 0) return null;
  return {
    tasks: groupByTask(scored, tasks),
    termFindings: result.termFindings ?? [],
  };
}

/** 報告タブ: 項目ごとに報告できたか。聞けたのに報告していない項目には報告の例。 */
export function buildReportReview(
  result: DentistReportScoringResult | null | undefined,
  patient: ScoringResult | null | undefined,
  tasks: ProgressSourceTask[] | null | undefined,
  itemExamples?: InstructionItemExamples | null,
): ReviewSide | null {
  if (!result?.itemScores?.length) return null;
  const heard = new Map(
    (patient?.itemScores ?? [])
      .filter((score) => score.score > 0)
      .map((score) => [exampleKeyOf(score) ?? score.itemId, score.evidence] as const),
  );
  const scored = result.itemScores
    .map((score) => {
      const key = exampleKeyOf(score) ?? score.itemId;
      const isCommunication = score.v31?.categoryId === "C0";
      const status: ReviewItemStatus = score.isReported
        ? score.wasProbedByDentist ? "assisted" : "done"
        : isCommunication || heard.has(key) ? "missing" : "not-heard";
      return scoredItem(score, {
        itemKey: score.v31?.itemKey ?? score.itemId,
        label: labelOf(score),
        level: score.v31?.levelKey ?? "",
        status,
        evidence: score.isReported ? score.evidence || undefined : heard.get(key) || undefined,
        examples: status === "missing" || status === "assisted" ? itemExamples?.[key]?.report : undefined,
      });
    })
    .filter((entry): entry is ScoredItem => entry !== null);
  if (scored.length === 0) return null;
  return {
    tasks: groupByTask(scored, tasks),
    termFindings: result.termFindings ?? [],
  };
}
